import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Grid, IconButton, Tooltip, Typography, styled } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PropTypes from 'prop-types';
import W86PlaceholderTextField from '../../W86Components/W86PlaceholderTextField';
import replacePlaceholderValue from '../../../utils/ReplacePlaceholderValue';
import { NODE_UI } from '../../NodeUI/nodeUiStyles';
import '../CredentialStoreStyle.css';

const DEFAULT_VALUE = 'apiKey';

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const Hint = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '6px',
}));

const PlaceholderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: NODE_UI.spacing.sm,
  padding: '6px 8px',
  borderRadius: NODE_UI.borderRadiusSm,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8f8f8',
}));

const Affix = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  userSelect: 'all',
}));

const InputWrap = styled(Box)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    fontFamily: 'monospace',
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  '& .MuiFormHelperText-root': {
    fontSize: NODE_UI.fontSize.caption,
    marginLeft: 0,
    marginTop: '4px',
  },
}));

const CopyBtn = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const SecretsPlaceholderField = ({
  label,
  instructionText,
  value,
  onChange,
  error,
  placeholderText,
  emptyFieldMessage,
  prefix = '',
  suffix = '.SECRET}',
  fieldKey,
  defaultValue = DEFAULT_VALUE,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [fieldValue, setFieldValue] = useState(value || defaultValue);

  const randomNumber = useMemo(() => {
    if (defaultValue === DEFAULT_VALUE) {
      return Math.floor(1000 + Math.random() * 9000);
    }
    return null;
  }, [defaultValue]);

  const generateDefaultValue = useCallback(() => {
    if (defaultValue === DEFAULT_VALUE && randomNumber) {
      return `${DEFAULT_VALUE}${randomNumber}`;
    }
    return defaultValue;
  }, [defaultValue, randomNumber]);

  useEffect(() => {
    if (!value) {
      const generatedDefault = generateDefaultValue();
      onChange(fieldKey, generatedDefault);
      setFieldValue(generatedDefault);
    } else {
      setFieldValue(value);
    }
  }, [value, fieldKey, onChange, defaultValue, generateDefaultValue]);

  const handleChange = e => {
    let newValue = replacePlaceholderValue(e.target.value);
    if (!newValue || newValue.trim() === '') {
      newValue = generateDefaultValue();
    }
    setFieldValue(newValue);
    onChange(fieldKey, newValue);
  };

  const copyToClipboard = () => {
    let placeholderValue = fieldValue;
    if (prefix && prefix.length > 0 && fieldValue && fieldValue.startsWith(prefix)) {
      placeholderValue = fieldValue;
    } else if (prefix) {
      placeholderValue = prefix + fieldValue;
    }
    const fullPlaceholder = `\${${placeholderValue}${suffix}`;

    navigator.clipboard
      .writeText(fullPlaceholder)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  return (
    <Grid item xs={12}>
      <Label>{label}</Label>
      {instructionText && <Hint>{instructionText}</Hint>}
      <PlaceholderRow>
        <Affix>{`\${${prefix}`}</Affix>
        <InputWrap>
          <W86PlaceholderTextField
            disableAdornment={true}
            size="small"
            fullWidth
            variant="outlined"
            value={fieldValue}
            error={error}
            placeholderText={placeholderText}
            handleChange={handleChange}
            helperText={error ? emptyFieldMessage : ''}
            excludePrefix={prefix}
            defaultValue={defaultValue}
          />
        </InputWrap>
        <Affix>{suffix}</Affix>
        <Tooltip title={copySuccess ? 'Copied!' : 'Copy full placeholder'} placement="top">
          <CopyBtn onClick={copyToClipboard} size="small" aria-label="copy placeholder">
            {copySuccess ? (
              <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            )}
          </CopyBtn>
        </Tooltip>
      </PlaceholderRow>
    </Grid>
  );
};

SecretsPlaceholderField.propTypes = {
  label: PropTypes.string.isRequired,
  instructionText: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  placeholderText: PropTypes.string.isRequired,
  emptyFieldMessage: PropTypes.string.isRequired,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  fieldKey: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
};

export default SecretsPlaceholderField;
